import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TakeReadingPopup from "@/components/readings/TakeReadingPopup";
import { toast } from "sonner";

interface TakeReadingControlProps {
  patient: any;
  patientId?: string;
  deviceType: string;
  deviceId: string | null;
  /** Admin viewing a facility patient must pick an assigned caretaker before taking a reading. */
  requireCaretakerSelection: boolean;
  onReadingReceived?: () => void;
  /** Optional extra actions rendered alongside (e.g. "Add Reading Manually"). */
  children?: React.ReactNode;
}

type CaretakerOption = { value: string; label: string };

const getCaretakerLabel = (caretaker: any): string => {
  const name = [caretaker?.firstName, caretaker?.middleName, caretaker?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || caretaker?.email || caretaker?._id || "Caretaker";
};

/**
 * Renders the "Take Reading" action for facility patients.
 * - Caretaker (self): shows the button directly.
 * - Admin: shows a single-select of assigned caretakers + Take Reading button.
 *   Clicking Take Reading without a caretaker selected shows an error toast.
 */
const TakeReadingControl: React.FC<TakeReadingControlProps> = ({
  patient,
  patientId,
  deviceType,
  deviceId,
  requireCaretakerSelection,
  onReadingReceived,
  children,
}) => {
  const [selectedCaretakerId, setSelectedCaretakerId] = useState<string>("");
  const [isTakeReadingOpen, setIsTakeReadingOpen] = useState(false);

  const caretakerOptions = useMemo<CaretakerOption[]>(() => {
    return (patient?.assignedCaretakerIds || [])
      .filter((c: any) => c && typeof c === "object" && c._id)
      .map((c: any) => ({ value: c._id, label: getCaretakerLabel(c) }));
  }, [patient?.assignedCaretakerIds]);

  const handleTakeReadingClick = () => {
    if (requireCaretakerSelection && !selectedCaretakerId) {
      toast.error("Please select a caretaker before taking a reading");
      return;
    }
    setIsTakeReadingOpen(true);
  };

  return (
    <>
      <div className="flex w-full flex-wrap items-center justify-end gap-2 md:w-fit">
        {requireCaretakerSelection && (
          <Select value={selectedCaretakerId} onValueChange={setSelectedCaretakerId}>
            <SelectTrigger className="h-9 w-[min(220px,60vw)] shrink-0 rounded-lg border-gray-300 capitalize">
              <SelectValue placeholder="Select caretaker" />
            </SelectTrigger>
            <SelectContent>
              {caretakerOptions.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  No assigned caretakers
                </div>
              ) : (
                caretakerOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="capitalize">
                    {o.label}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        )}

        <Button variant="outline" onClick={handleTakeReadingClick}>
          Take Reading
        </Button>

        {children}
      </div>

      {patientId && (
        <TakeReadingPopup
          isOpen={isTakeReadingOpen}
          onClose={() => setIsTakeReadingOpen(false)}
          patientId={patientId}
          deviceType={deviceType}
          deviceId={deviceId}
          caretakerId={requireCaretakerSelection ? selectedCaretakerId : undefined}
          onReadingReceived={onReadingReceived}
        />
      )}
    </>
  );
};

export default TakeReadingControl;
